// Ported verbatim from v1 (ChatLog.collage.ts) together with its tests: pure
// layout math for 1/2/3/4+ image collages inside a fixed-width envelope.
const COLLAGE_GUTTER = 2
const COLLAGE_MIN_ASPECT = 4 / 5
const COLLAGE_MAX_ASPECT = 16 / 10
const COLLAGE_MULTI_ASPECT = 1

export interface ICollageTile {
  index: number
  left: number
  top: number
  width: number
  height: number
}

export interface ICollageLayout {
  width: number
  height: number
  tiles: ICollageTile[]
  overlayCount: number
}

interface ISizedImage {
  width?: number
  height?: number
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

const getSingleTileHeight = (
  image: ISizedImage,
  envelopeWidth: number,
): number => {
  if (!image?.width || !image?.height) {
    return envelopeWidth
  }

  const aspect = clamp(
    image.width / image.height,
    COLLAGE_MIN_ASPECT,
    COLLAGE_MAX_ASPECT,
  )
  return Math.round(envelopeWidth / aspect)
}

export const getCollageLayout = (
  images: readonly ISizedImage[],
  envelopeWidth: number,
): ICollageLayout => {
  const count = images?.length || 0

  if (count === 1) {
    const height = getSingleTileHeight(images[0], envelopeWidth)
    return {
      width: envelopeWidth,
      height,
      overlayCount: 0,
      tiles: [{ index: 0, left: 0, top: 0, width: envelopeWidth, height }],
    }
  }

  const envelopeHeight = Math.round(envelopeWidth / COLLAGE_MULTI_ASPECT)
  const halfWidth = (envelopeWidth - COLLAGE_GUTTER) / 2
  const rightLeft = halfWidth + COLLAGE_GUTTER
  const rightWidth = envelopeWidth - rightLeft

  if (count === 2) {
    return {
      width: envelopeWidth,
      height: envelopeHeight,
      overlayCount: 0,
      tiles: [
        { index: 0, left: 0, top: 0, width: halfWidth, height: envelopeHeight },
        {
          index: 1,
          left: rightLeft,
          top: 0,
          width: rightWidth,
          height: envelopeHeight,
        },
      ],
    }
  }

  const topHeight = (envelopeHeight - COLLAGE_GUTTER) / 2
  const bottomTop = topHeight + COLLAGE_GUTTER
  const bottomHeight = envelopeHeight - bottomTop

  if (count === 3) {
    return {
      width: envelopeWidth,
      height: envelopeHeight,
      overlayCount: 0,
      tiles: [
        { index: 0, left: 0, top: 0, width: halfWidth, height: envelopeHeight },
        {
          index: 1,
          left: rightLeft,
          top: 0,
          width: rightWidth,
          height: topHeight,
        },
        {
          index: 2,
          left: rightLeft,
          top: bottomTop,
          width: rightWidth,
          height: bottomHeight,
        },
      ],
    }
  }

  return {
    width: envelopeWidth,
    height: envelopeHeight,
    overlayCount: count > 4 ? count - 4 : 0,
    tiles: [
      { index: 0, left: 0, top: 0, width: halfWidth, height: topHeight },
      {
        index: 1,
        left: rightLeft,
        top: 0,
        width: rightWidth,
        height: topHeight,
      },
      {
        index: 2,
        left: 0,
        top: bottomTop,
        width: halfWidth,
        height: bottomHeight,
      },
      {
        index: 3,
        left: rightLeft,
        top: bottomTop,
        width: rightWidth,
        height: bottomHeight,
      },
    ],
  }
}
